import { useId, useState } from 'react';
import { IconMoodSmile } from '@tabler/icons-react';
import { IconButton } from '../../primitives/IconButton.js';
import { Popover } from '../../primitives/Popover.js';
import { Text } from '../../primitives/Text.js';
import { cx } from '../../utils/cx.js';
import { EMOJI_CATEGORIES } from './emojiPickerData.js';
import styles from './EmojiPicker.module.css';

export type EmojiPickerProps = {
  onSelect: (emoji: string) => void;
  disabled?: boolean;
  align?: 'start' | 'end';
};

export function EmojiPicker({ onSelect, disabled = false, align = 'start' }: EmojiPickerProps) {
  const titleId = useId();
  const [open, setOpen] = useState(false);
  const [categoryId, setCategoryId] = useState(EMOJI_CATEGORIES[0]?.id ?? 'smileys');
  const activeCategory =
    EMOJI_CATEGORIES.find((category) => category.id === categoryId) ?? EMOJI_CATEGORIES[0];

  const pick = (emoji: string) => {
    onSelect(emoji);
    setOpen(false);
  };

  return (
    <Popover
      opened={open}
      onChange={setOpen}
      portal
      align={align}
      portalMinWidth={280}
      portalMaxWidth={300}
      portalEstimatedHeight={300}
      portalZIndex="calc(var(--wp-z-tooltip) + 1)"
      target={
        <IconButton
          type="button"
          aria-label="Emoji"
          variant="ghost"
          size="sm"
          disabled={disabled}
        >
          <IconMoodSmile size={16} stroke={1.8} aria-hidden />
        </IconButton>
      }
    >
      <div className={styles.shell}>
        <Text id={titleId} size="xs" fw={600} c="dimmed">
          Emoji
        </Text>
        <div className={styles.tabs} role="tablist" aria-labelledby={titleId}>
          {EMOJI_CATEGORIES.map((category) => (
            <button
              key={category.id}
              type="button"
              role="tab"
              aria-selected={category.id === activeCategory?.id}
              aria-label={category.label}
              className={cx(styles.tab, category.id === activeCategory?.id && styles.tabActive)}
              onClick={() => setCategoryId(category.id)}
            >
              {category.icon}
            </button>
          ))}
        </div>
        <div className={styles.grid} role="tabpanel">
          {activeCategory?.emojis.map((emoji) => (
            <button
              key={`${activeCategory.id}-${emoji}`}
              type="button"
              className={styles.emojiButton}
              onClick={() => pick(emoji)}
              aria-label={`Insérer ${emoji}`}
            >
              {emoji}
            </button>
          ))}
        </div>
      </div>
    </Popover>
  );
}
